import { datadogRum } from '@datadog/browser-rum';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';

import { getAuthToken } from '@/helpers/Authentication/authClient';
import { ApiTypes, logErrorToDatadogCustomMetric } from '@/helpers/LogHelper';
import { CustomPOIRequestStatus } from '@/helpers/PointOfInterests/types';
import {
  fetchSurroundingInfo,
  getTravelTimeEntryPointHeaders,
  mapCustomPOIResponse,
  processSurroundingInfoCore,
  SURROUNDING_INFO_TIMEOUT_MS,
  travelTimeAxiosInstance,
  TravelTimeEntryPoint,
} from '@/helpers/travel-time-helpers';
import {
  CustomPOI,
  TravelTimeData,
  TravelTimeDataCollection,
  TravelTimePOIKey,
  TravelTimeTransportationType,
} from '@/store/types/travel-time-state';
import { GeoCoordinates } from '@smg-real-estate/hg-rets-schema/dist/esm/model/HgRetsListing';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useTravelTimeStore = defineStore('travelTime', () => {
  const data = ref<TravelTimeDataCollection | null>(null);
  const customPOIData = ref<TravelTimeDataCollection>({});
  const customPOI = ref<CustomPOI | null>(null);
  const hasDefaultPOI = ref<Listing[]>([]);
  const hasUserPOI = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const showCustomPoiModal = ref<boolean>(false);
  const showCustomPoiSignInModal = ref<boolean>(false);
  const requestStatus = ref<CustomPOIRequestStatus>(CustomPOIRequestStatus.NOT_SUBMITTED);

  // mutations

  const setShowCustomPoiModal = (payload: boolean) => {
    showCustomPoiModal.value = payload;
  };

  const setShowCustomPoiSignInModal = (payload: boolean) => {
    showCustomPoiSignInModal.value = payload;
  };

  const setCustomPOIModalRequestStatus = (payload: CustomPOIRequestStatus) => {
    requestStatus.value = payload;
  };

  const setTravelTimeData = (
    payload: { listingId?: string; data?: TravelTimeData } | null,
  ): void => {
    if (!payload?.listingId) {
      data.value = {};
      return;
    }

    const { listingId, data: listingData } = payload;
    if (!data.value) {
      data.value = {};
    }
    data.value[listingId] = listingData || [];
  };

  const setCustomPOITravelTimeData = (
    payload: { listingId: string; data: TravelTimeData }[] | null,
  ): void => {
    if (!payload) {
      customPOIData.value = {};
      return;
    }

    payload.forEach((item) => {
      const { listingId, data: listingData } = item;
      customPOIData.value[listingId] = listingData;
    });
  };

  const setCustomPOI = (payload: CustomPOI | null) => {
    customPOI.value = payload;
  };

  const setHasDefaultPOI = (payload: Listing[]) => {
    hasDefaultPOI.value = payload;
  };

  const setHasUserPOI = (payload: boolean) => {
    hasUserPOI.value = payload;
  };

  const setLoading = (payload: boolean) => {
    loading.value = payload;
  };

  // getters
  const getTravelTimeData = (listingId: string): TravelTimeData => data.value?.[listingId] ?? [];

  const getCustomPOITravelTimeData = (listingId: string): TravelTimeData =>
    customPOIData.value?.[listingId] ?? [];

  const getCustomPOI = computed((): CustomPOI | null => customPOI.value);

  // actions

  const fetchTravelTime = async ({
    listingId,
    geoCoordinates,
    persona,
  }: {
    listingId: string;
    geoCoordinates: GeoCoordinates;
    persona?: string;
  }) => {
    try {
      const { latitude, longitude } = geoCoordinates;

      // Type guard: Ensure coordinates are defined before making API call
      if (latitude === undefined || longitude === undefined) {
        setTravelTimeData({ listingId, data: [] });
        return;
      }

      const result = await fetchSurroundingInfo(listingId, latitude, longitude, {
        entryPoint: TravelTimeEntryPoint.TRAVEL_TIME,
        persona,
      });

      if (result.isTimeout) {
        handleSurroundingInfoTimeout(listingId);
        setTravelTimeData({ listingId, data: [] });
        return;
      }

      if (result.error) {
        throw result.error;
      }

      if (!result.data?.data) {
        setTravelTimeData({ listingId, data: [] });
        return;
      }

      const travelTimings = processSurroundingInfoResponse(result.data.data);
      setTravelTimeData({ listingId, data: travelTimings });
    } catch (error) {
      logErrorToDatadogCustomMetric(ApiTypes.poi_api, { listingId }, error);
      setTravelTimeData(null);
    }
  };

  const fetchCustomPOITravelTimeData = async ({ listings }: { listings: Listing[] }) => {
    try {
      const endpoint = new URL(
        `${import.meta.env.VUE_POINTS_OF_INTEREST_API_ENDPOINT}/custom-points/default`,
      );
      const requestPayload = {
        transportType: [TravelTimeTransportationType.WALKING],
        poi: [TravelTimePOIKey.TRAIN_STATIONS],
        listings: listings.map((listing) => ({
          listingId: listing.id,
          lat: listing.address?.geoCoordinates?.latitude,
          lng: listing.address?.geoCoordinates?.longitude,
        })),
        lang: HgSupportedLanguage.EN,
      };

      const response = await travelTimeAxiosInstance.post(endpoint.toString(), requestPayload, {
        headers: getTravelTimeEntryPointHeaders(TravelTimeEntryPoint.TRAVEL_TIME),
      });
      const commitPayload = mapCustomPOIResponse(response);

      setCustomPOITravelTimeData(commitPayload);
      setHasDefaultPOI(filterEmptyListings(listings, commitPayload));
    } catch (e) {
      setCustomPOITravelTimeData(null);
    }
  };

  const fetchDefaultPOIDataNoCommit = async ({ listing }: { listing: Listing }) => {
    try {
      const authToken = await getAuthToken();

      if (!authToken) {
        return;
      }
      const endpoint = new URL(
        `${import.meta.env.VUE_POINTS_OF_INTEREST_API_ENDPOINT}/custom-points/default`,
      );
      const requestPayload = {
        transportType: [TravelTimeTransportationType.WALKING],
        poi: [TravelTimePOIKey.TRAIN_STATIONS],
        listings: [
          {
            listingId: listing.id,
            lat: listing.address?.geoCoordinates?.latitude,
            lng: listing.address?.geoCoordinates?.longitude,
          },
        ],
        lang: HgSupportedLanguage.EN,
      };

      const response = await travelTimeAxiosInstance.post(endpoint.toString(), requestPayload, {
        headers: getTravelTimeEntryPointHeaders(TravelTimeEntryPoint.TRAVEL_TIME),
      });
      const commitPayload = mapCustomPOIResponse(response);
      setHasDefaultPOI(filterEmptyListings([listing], commitPayload));
    } catch (e) {
      setHasDefaultPOI([]);
    }
  };

  const loadCustomPOI = async ({ listings }: { listings: Listing[] }) => {
    try {
      // KK-1450: Remove blocking setLoading(true) for better performance
      // Execute POI loading without blocking the main thread
      const promises: Promise<any>[] = [];

      if (await getAuthToken()) {
        promises.push(fetchUserPOITravelTimeData({ listings }));
      } else if (!customPOI.value) {
        promises.push(fetchCustomPOITravelTimeData({ listings }));
      }

      // Wait for all operations to complete without blocking UI
      await Promise.allSettled(promises);
    } catch (e) {
      // Handle errors gracefully without blocking UI
      setCustomPOITravelTimeData(null);
    }
  };

  const fetchUserPOITravelTimeData = async ({ listings }: { listings: Listing[] }) => {
    try {
      // KK-1450: Reduce blocking setLoading calls for better performance
      const authToken = await getAuthToken();
      if (!authToken) {
        return;
      }

      const endpoint = new URL(
        `${import.meta.env.VUE_POINTS_OF_INTEREST_API_ENDPOINT}/custom-points/calculate`,
      );

      const response = await travelTimeAxiosInstance.post(
        endpoint.toString(),
        listings.map((listing) => ({
          listingId: listing.id,
          lat: listing.address?.geoCoordinates?.latitude,
          lng: listing.address?.geoCoordinates?.longitude,
        })),
        {
          headers: {
            ...getTravelTimeEntryPointHeaders(TravelTimeEntryPoint.TRAVEL_TIME),
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (!response.data || (listings[0].id && !response.data[listings[0].id])) {
        // Fallback to default POI data without blocking
        Promise.resolve().then(() => fetchCustomPOITravelTimeData({ listings }));
        setHasUserPOI(false);
      } else {
        const commitPayload = mapCustomPOIResponse(response);
        setCustomPOITravelTimeData(commitPayload);
        setHasUserPOI(true);
      }
    } catch (e) {
      setHasUserPOI(false);
      // Fallback to default POI data in background
      Promise.resolve().then(() => fetchCustomPOITravelTimeData({ listings }));
    }
  };

  const fetchCustomPOI = async () => {
    // KK-1450: Remove blocking setLoading for better performance
    const authToken = await getAuthToken();

    if (!authToken) {
      setCustomPOI(null);
      return;
    }

    try {
      const response = await travelTimeAxiosInstance.get(
        `${import.meta.env.VUE_POINTS_OF_INTEREST_API_ENDPOINT}/custom-points`,
        {
          headers: {
            ...getTravelTimeEntryPointHeaders(TravelTimeEntryPoint.TRAVEL_TIME),
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      setCustomPOI(response.data?.points?.[0] ?? null);
    } catch (error) {
      datadogRum.addError(error);
      setCustomPOI(null);
    }
  };

  const createCustomPOI = async ({
    name,
    transportationType,
    address,
    lat,
    lng,
  }: Omit<CustomPOI, 'id'>) => {
    setLoading(true);
    const authToken = await getAuthToken();

    if (!authToken) {
      setCustomPOI(null);
      setLoading(false);
      return;
    }

    try {
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.PENDING);

      const response = await travelTimeAxiosInstance.post(
        `${import.meta.env.VUE_POINTS_OF_INTEREST_API_ENDPOINT}/custom-points`,
        {
          name,
          transportationType,
          address,
          lat,
          lng,
        },
        {
          headers: {
            ...getTravelTimeEntryPointHeaders(TravelTimeEntryPoint.TRAVEL_TIME),
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      setCustomPOI(response.data?.points[0]);
      setLoading(false);
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.SUCCESS);
      return response.data?.points[0];
    } catch (error) {
      datadogRum.addError(error);
      setCustomPOI(null);
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.ERROR);
      setLoading(false);
    }
  };

  const updateCustomPOI = async (customPoi: CustomPOI) => {
    const authToken = await getAuthToken();

    if (!authToken) {
      setCustomPOI(null);
      return;
    }

    try {
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.PENDING);
      const response = await travelTimeAxiosInstance.put(
        `${import.meta.env.VUE_POINTS_OF_INTEREST_API_ENDPOINT}/custom-points`,
        {
          pointItem: customPoi,
        },
        {
          headers: {
            ...getTravelTimeEntryPointHeaders(TravelTimeEntryPoint.TRAVEL_TIME),
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      setCustomPOI(response.data?.points[0]);
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.SUCCESS);
      return response.data?.points[0];
    } catch (error) {
      datadogRum.addError(error);
      setCustomPOI(null);
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.ERROR);
    }
  };

  const deleteCustomPOI = async ({ id, listings }: { id: string; listings?: Listing[] }) => {
    setLoading(true);
    const authToken = await getAuthToken();

    if (!authToken || !id) {
      setLoading(false);
      return;
    }

    try {
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.PENDING);

      await travelTimeAxiosInstance.delete(
        `${import.meta.env.VUE_POINTS_OF_INTEREST_API_ENDPOINT}/custom-points/${id}`,
        {
          headers: {
            ...getTravelTimeEntryPointHeaders(TravelTimeEntryPoint.TRAVEL_TIME),
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (listings) {
        // call loadCustomPOI from here to avoid flickering between loading and final POI data https://smg-re.atlassian.net/browse/RBD-2790
        await loadCustomPOI({ listings });
      }

      setHasUserPOI(false);
      setCustomPOI(null);
      setLoading(false);
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.SUCCESS);
    } catch (error) {
      datadogRum.addError(error);
      setCustomPOI(null);
      setLoading(false);
      setCustomPOIModalRequestStatus(CustomPOIRequestStatus.ERROR);
    }
  };

  const resetTravelTimeRequestStatus = () => {
    setCustomPOIModalRequestStatus(CustomPOIRequestStatus.NOT_SUBMITTED);
  };

  return {
    // state
    data,
    customPOIData,
    customPOI,
    hasDefaultPOI,
    hasUserPOI,
    loading,
    showCustomPoiModal,
    showCustomPoiSignInModal,
    requestStatus,

    // mutations
    setShowCustomPoiModal,
    setShowCustomPoiSignInModal,
    setCustomPOIModalRequestStatus,
    setTravelTimeData,
    setCustomPOITravelTimeData,
    setCustomPOI,
    setHasDefaultPOI,
    setHasUserPOI,
    setLoading,

    // getters
    getTravelTimeData,
    getCustomPOITravelTimeData,
    getCustomPOI,

    // actions
    fetchTravelTime,
    fetchCustomPOITravelTimeData,
    fetchDefaultPOIDataNoCommit,
    loadCustomPOI,
    fetchUserPOITravelTimeData,
    fetchCustomPOI,
    createCustomPOI,
    updateCustomPOI,
    deleteCustomPOI,
    resetTravelTimeRequestStatus,
  };
});

// Helper to handle API timeout logging
function handleSurroundingInfoTimeout(listingId: string): void {
  console.warn(`Surrounding info fetch timeout for listing ${listingId}`);
  datadogRum.addAction('surrounding_info_timeout', {
    listingId,
    timeout: SURROUNDING_INFO_TIMEOUT_MS,
  });
}

const filterEmptyListings = (
  allListings: Listing[],
  listingsWithData: { listingId: string; data: TravelTimeData }[],
): Listing[] => {
  return allListings.filter((listing) => {
    return listingsWithData.some((data) => data.listingId === listing.id);
  });
};

/**
 * Process surrounding info response for the old "Surrounding information" section
 * This function ALWAYS filters to only show the 4 base POI types
 */
const processSurroundingInfoResponse = (data: any): TravelTimeData => {
  // KK-1992: ALWAYS filter to only show the 4 base POI types for the old "Umgebungsinformationen" section
  // Persona-specific POIs (park, playground, cafe, etc.) should NOT appear here
  return processSurroundingInfoCore(data, true);
};

/**
 * Process surrounding info response for the Neighbourhood Insights feature
 * This function includes ALL POI types (base + persona-specific)
 * Exported for use by neighbourhood store
 */
export const processPersonaSurroundingInfoResponse = (data: any): TravelTimeData => {
  // Include ALL POI types (no filtering) for Neighbourhood Insights
  return processSurroundingInfoCore(data, false);
};
